import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { usePopover } from '@adornis/functional-lit/use-popover.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { formatBillingPositionPrice } from '../../../../billingPositions/client/BillingPositionPriceInput.js';
import { getMandatePhase, isFunded, MandatePhase } from '../../../../sepa/api/MandatStatus.js';
import { SepaAPI } from '../../../../sepa/api/SepaAPI.js';
import type { SepaMandat } from '../../../../sepa/entities/SepaMandat.js';
import type { GroupParticipantRow } from '../../api/GroupsDetailsAPI.js';

/**
 * The badge colour tells at a glance whether the collection is still going on: one mandate running
 * today makes it green, one that only starts later primary, a collected one-time mandate violet, and if every mandate is over the badge
 * gets an error outline. Tailwind only sees literal class tokens, so every tone spells its classes
 * out instead of interpolating the colour name.
 */
const BADGE_TONES = {
  current: {
    container: 'border-success/35',
    icon: 'text-success',
    button: 'border-success/35 text-success/80 hover:bg-success/10 hover:border-success/60',
  },
  future: {
    container: 'border-primary/35',
    icon: 'text-primary',
    button: 'border-primary/35 text-primary/80 hover:bg-primary/10 hover:border-primary/60',
  },
  billed: {
    container: 'border-violet-600/35',
    icon: 'text-violet-600',
    button: 'border-violet-600/35 text-violet-600/80 hover:bg-violet-600/10 hover:border-violet-600/60',
  },
  past: {
    container: 'border-error/35',
    icon: 'text-error',
    button: 'border-error/35 text-error/80 hover:bg-error/10 hover:border-error/60',
  },
} as const;

/** The badge tone of each phase - a template is never shown (`relevantMandates`). */
const TONE_OF_PHASE: Record<MandatePhase, keyof typeof BADGE_TONES> = {
  [MandatePhase.RUNNING]: 'current',
  [MandatePhase.PLANNED]: 'future',
  [MandatePhase.BILLED]: 'billed',
  [MandatePhase.ENDED]: 'past',
  [MandatePhase.TEMPLATE]: 'past',
};
const getMandateTone = (mandate: SepaMandat) => TONE_OF_PHASE[getMandatePhase(mandate)];

const formatRuntime = (mandate: Pick<SepaMandat, 'start' | 'end'>) =>
  `${mandate.start ? formatCalendarDay(mandate.start, 'dd.MM.yyyy') : 'offen'} - ${mandate.end ? formatCalendarDay(mandate.end, 'dd.MM.yyyy') : 'offen'}`;

export const MemberInlineSepaPaymentMethod = stateful(
  (props: { participantRow: GroupParticipantRow; disabled: boolean }) => {
    // every booking of the row, so a mandate of a past or upcoming school year shows too
    const bookingIDs = props.participantRow.bookings.map(booking => booking._id);
    const { value: sepaMandatesForBooking, loading: sepaMandatesLoading } = useQuery(
      () =>
        SepaAPI.subscribeForBookings(bookingIDs)({
          _id: 1,
          accepted: 1,
          amountInCents: 1,
          end: 1,
          start: 1,
          rhythm: 1,
          billedOn: 1,
          bookingID: 1,
          acceptedPersonName: 1,
        }),
      [bookingIDs.join()],
    );
    // the runtimes open on click and stay open for scrolling, so a popover instead of a hover tooltip
    const runtimes = usePopover({ positionArea: 'bottom', closeOnClickOutside: true });

    if (sepaMandatesLoading) {
      return html`
        <div class=${['flex items-center py-1.5 mx-1', props.disabled ? 'opacity-70' : ''].join(' ')}>
          <span class="loading loading-spinner loading-sm text-base-content/70"></span>
        </div>
      `;
    }

    const mandates = sepaMandatesForBooking ?? [];
    /**
     * The accepted mandates - everything the badge summarizes is derived from these. An ended
     * funding is left out: the booking is no Selbstzahler because of it.
     */
    const relevantMandates = (mandates as SepaMandat[]).filter(mandate => {
      const phase = getMandatePhase(mandate);
      return phase !== MandatePhase.TEMPLATE && !(phase === MandatePhase.ENDED && isFunded(mandate));
    });
    const hasMultipleMandates = mandates.length > 1;
    const runtimeTooltipRows = mandates.map(
      mandate =>
        `${formatRuntime(mandate)} ${isNotNullish(mandate.amountInCents) ? `(${formatBillingPositionPrice(mandate.amountInCents)})` : ''}`,
    );

    const historyButton = (buttonClass: string) =>
      hasMultipleMandates
        ? html`
            <button
              type="button"
              class=${`btn btn-ghost btn-xs btn-circle border ${buttonClass}`}
              aria-label="Mandats-Laufzeiten"
              style="anchor-name: ${runtimes.anchorName};"
              ${ref(runtimes.anchorRef)}
              @click=${(e: Event) => {
                e.stopPropagation();
                if (runtimes.visible) runtimes.hide();
                else runtimes.show();
              }}
            >
              <span class="icon-[material-symbols--history-rounded] text-base"></span>
            </button>
            <div
              popover="manual"
              tabindex="-1"
              id=${runtimes.popoverId}
              style="position-anchor: ${runtimes.anchorName}; ${runtimes.getAnchorStyles('position-try-fallbacks: flip-block;')}"
              class="bg-base-100 text-base-content shadow-2xl border border-base-300 rounded-box p-2"
              ${ref(runtimes.popoverRef)}
              @click=${(e: Event) => e.stopPropagation()}
            >
              <div class="text-sm font-semibold text-base-content mb-1">Mandats-Laufzeiten</div>
              <div class="max-w-72 max-h-56 overflow-y-auto pr-1 flex flex-col gap-0.5 text-sm text-base-content/80">
                ${runtimeTooltipRows.map(row => html`<span>${row}</span>`)}
              </div>
            </div>
          `
        : '';

    /**
     * One SEPA badge over `shown`. Its colour answers "is anything still coming?" over `toneSource`:
     * one running mandate wins over an upcoming one, and only when all of them lie in the past does
     * the badge read as over. The amount is what actually gets collected and may deviate from the
     * price of the billing position the mandate was created from.
     */
    const renderBadge = (shown: SepaMandat[], toneSource: SepaMandat[], payer?: Maybe<string>, history = true) => {
      const toneKeys = toneSource.map(getMandateTone);
      const tone = BADGE_TONES[(['current', 'future', 'billed'] as const).find(t => toneKeys.includes(t)) ?? 'past'];

      const uniqueAmountsInCents = [...new Set(shown.map(mandate => mandate.amountInCents).filter(isNotNullish))];
      const firstAmountInCents = uniqueAmountsInCents[0];
      const amountLabel = isNotNullish(firstAmountInCents)
        ? `${formatBillingPositionPrice(firstAmountInCents)}${uniqueAmountsInCents.length > 1 ? '+' : ''}`
        : '';
      // one runtime fits the badge: the running mandate's, else the latest one's - all of them are in the history
      const runtimeMandate =
        shown.find(mandate => getMandatePhase(mandate) === MandatePhase.RUNNING) ??
        [...shown].sort((a, b) => (b.start?.getTime() ?? 0) - (a.start?.getTime() ?? 0))[0];

      return html`
        <div
          class=${[
            'inline-flex items-center gap-2 rounded-lg border bg-base-100 px-3 py-1.5 shadow-sm mx-1',
            tone.container,
            props.disabled ? 'opacity-70' : '',
          ].join(' ')}
        >
          <span class=${`icon-[material-symbols--credit-card-heart] text-base ${tone.icon}`}></span>
          <span class="text-sm font-medium text-base-content">SEPA</span>
          ${amountLabel ? html`<span class="text-sm font-semibold text-base-content">${amountLabel}</span>` : ''}
          ${
            runtimeMandate
              ? html`<span class="text-sm text-base-content/70 whitespace-nowrap"
                  >${formatRuntime(runtimeMandate)}</span
                >`
              : ''
          }
          ${payer ? html`<span class="text-sm text-base-content/70 max-w-40 truncate" title=${payer}>${payer}</span>` : ''}
          ${history ? historyButton(tone.button) : ''}
        </div>
      `;
    };

    /**
     * Two payers may share one booking (each pledging their part), and both are collected, so each
     * running mandate gets its own badge with its payer instead of a summary that would read like
     * one collection. Ended ones stay in the history.
     */
    const runningMandates = relevantMandates.filter(mandate => {
      const tone = getMandateTone(mandate);
      return tone === 'current' || tone === 'future';
    });
    if (runningMandates.length > 1) {
      return html`
        <div class="flex flex-col items-start gap-1 py-1">
          ${runningMandates.map((mandate, index) =>
            renderBadge([mandate], [mandate], mandate.acceptedPersonName, index === runningMandates.length - 1),
          )}
        </div>
      `;
    }

    if (relevantMandates.length) return renderBadge(relevantMandates, mandates as SepaMandat[]);

    return html`
      <div
        class=${[
          'inline-flex items-center gap-2 rounded-lg border border-base-300/80 bg-base-100 px-3 py-1.5 shadow-sm mx-1',
          props.disabled ? 'opacity-70' : '',
        ].join(' ')}
      >
        <span class="icon-[material-symbols--payments-outline-rounded] text-base-content/70 text-base"></span>
        <span class="text-sm font-medium text-base-content/90">Selbstzahler</span>
        ${historyButton('border-base-300 text-base-content/70 hover:bg-base-200/70 hover:border-base-300')}
      </div>
    `;
  },
);
